import {
  createDailyWorkload,
  dateToPBDate,
  dateToString,
  timestampDateOptional,
  WorkloadData,
  WorkloadGroupDetails,
  WorkloadGroupsInfo,
  WorkloadPublishedWorkInfo,
  WorkloadSummaryInfo
} from '@/models';
import { ServiceContainer } from '@/providers';
import { PlannerCalendarStore } from '@/stores';
import { CourseSectionDetails } from '@buf/studyo_studyo-today-planners.bufbuild_es/studyo/today/planners/v1/resources/course_section_details_pb';
import { CourseSectionRole } from '@buf/studyo_studyo-today-planners.bufbuild_es/studyo/today/planners/v1/resources/course_section_role_pb';
import { DailyWorkload } from '@buf/studyo_studyo-today-schools.bufbuild_es/studyo/today/schools/v1/resources/daily_workload_pb';
import { PublishedWork } from '@buf/studyo_studyo-today-schools.bufbuild_es/studyo/today/schools/v1/resources/published_work_pb';
import { WorkloadGroup } from '@buf/studyo_studyo-today-schools.bufbuild_es/studyo/today/schools/v1/resources/workload_group_pb';
import { WorkloadStatus } from '@buf/studyo_studyo-today-schools.bufbuild_es/studyo/today/schools/v1/resources/workload_status_pb';
import { Timestamp, timestampDate } from '@bufbuild/protobuf/wkt';
import { isWithinInterval } from 'date-fns';
import { chain } from 'lodash';
import { computed, makeObservable } from 'mobx';
import { UpdatableViewModelState } from '../shared';
import { AppBaseUpdatableDialogViewModel, UpdatableDialogViewModel } from '../utils';

interface Period {
  start: Timestamp;
  end: Timestamp;
  label: string;
}

export interface CourseSectionWorkloadInfoViewModel extends UpdatableDialogViewModel {
  readonly courseSection: CourseSectionDetails;
  getWorkloadGroups(): WorkloadGroupsInfo;
  getCourseSectionWorkloadInformationForDate(): WorkloadData<WorkloadSummaryInfo>;
  getPublishedWorkInfo(workId: string): WorkloadPublishedWorkInfo | undefined;
}

export class AppCourseSectionWorkloadInfoViewModel
  extends AppBaseUpdatableDialogViewModel
  implements CourseSectionWorkloadInfoViewModel
{
  private readonly _calendarStore: PlannerCalendarStore;

  constructor(
    private readonly _plannerId: string,
    private readonly _date: Date,
    private readonly _courseSectionId: string,
    private readonly _schoolId: string,
    onClose: () => Promise<void>,
    localization = ServiceContainer.services.localization,

    private readonly _plannerStore = ServiceContainer.services.plannerStore,
    private readonly _workloadStore = ServiceContainer.services.workloadStore
  ) {
    super(localization, onClose);
    this._calendarStore = this._plannerStore.getCalendarStore(this._plannerId);
    makeObservable(this);
  }

  @computed
  private get allClasses(): CourseSectionDetails[] {
    return this._plannerStore.getCourseSectionsInPlanner(this._plannerId).values;
  }

  @computed
  private get workloadLoadable() {
    return this._workloadStore.getCourseSectionWorkloadLoadableForDate(
      this._courseSectionId,
      this._schoolId,
      dateToPBDate(this._date)
    );
  }

  @computed
  get courseSection(): CourseSectionDetails {
    const cs = this.allClasses.find(
      (c) => c.schoolsCourseSection?.id === this._courseSectionId && c.schoolsCourseSection.schoolId === this._schoolId
    );

    if (cs == null) {
      throw new Error(`Course section not found for id ${this._courseSectionId}`);
    }

    return cs;
  }

  @computed
  private get dailyWorkload(): DailyWorkload {
    return this.workloadLoadable.data.dailyWorkloads.at(0) ?? createDailyWorkload();
  }

  @computed
  get state(): UpdatableViewModelState {
    return this.workloadLoadable.state;
  }

  @computed
  get hasData(): boolean {
    return this.workloadLoadable.hasData;
  }

  readonly hasChanges = false;
  readonly isSubmitting = false;

  reloadData(): Promise<void> {
    return this.workloadLoadable.fetch(true);
  }

  getWorkloadGroups(): WorkloadGroupsInfo {
    const aboveThresholdGroups = this.dailyWorkload.groups.filter((g) => g.status === WorkloadStatus.ABOVE_THRESHOLD);
    const atThresholdGroups = this.dailyWorkload.groups.filter((g) => g.status === WorkloadStatus.AT_THRESHOLD);
    const underThresholdGroups = this.dailyWorkload.groups.filter((g) => g.status === WorkloadStatus.UNDER_THRESHOLD);

    return {
      aboveThresholdGroups: aboveThresholdGroups.map((g) => this.convertGroupToInfo(g)),
      atThresholdGroups: atThresholdGroups.map((g) => this.convertGroupToInfo(g)),
      underloadedGroups: underThresholdGroups.map((g) => this.convertGroupToInfo(g))
    };
  }

  getCourseSectionWorkloadInformationForDate(): WorkloadData<WorkloadSummaryInfo> {
    return this._workloadStore.getCourseSectionWorkloadInformationForDate(
      this._courseSectionId,
      this._schoolId,
      dateToPBDate(this._date)
    );
  }

  getPublishedWorkInfo(workId: string): WorkloadPublishedWorkInfo | undefined {
    for (const group of this.dailyWorkload.groups) {
      for (const work of group.importantPublishedWorks) {
        if (work.id === workId) {
          const periods = this.getPeriodsForDate();
          return this.mapPublishedWorkToInfo(work, periods);
        }
      }
    }

    return undefined;
  }

  private convertGroupToInfo(group: WorkloadGroup): WorkloadGroupDetails {
    const periods = this.getPeriodsForDate();
    const works = group.importantPublishedWorks.map((pw) => this.mapPublishedWorkToInfo(pw, periods));
    return { works, students: group.students, status: group.status };
  }

  private mapPublishedWorkToInfo(work: PublishedWork, periods: Period[]): WorkloadPublishedWorkInfo {
    const courseSection = this._workloadStore.getCourseSection(work.courseSectionId, work.schoolId);
    const plannerCourseSection = this.allClasses.find(
      (cs) => cs.schoolsCourseSection?.id === work.courseSectionId && cs.schoolsCourseSection.schoolId === work.schoolId
    );
    const periodTag = this.getPeriodLabelForDate(timestampDateOptional(work.dueTime), work.isDueAllDay, periods);
    return {
      publishedWork: work,
      courseSection,
      periodTag,
      plannerCourseSection,
      isTaughtSection: plannerCourseSection?.role === CourseSectionRole.TEACHER
    };
  }

  private getPeriodsForDate(): Period[] {
    const items = this._calendarStore.days.get(dateToString(this._date))?.items;
    return chain(items)
      .map((i) =>
        i.item.case === 'courseSectionOccurrence' && !i.isContextualAllDay
          ? { start: i.contextualStartTime!, end: i.contextualEndTime!, label: i.item.value.periodLabel }
          : undefined
      )
      .compact()
      .value();
  }

  private getPeriodLabelForDate(date: Date | undefined, isAllDay: boolean, periods: Period[]): string | undefined {
    if (date == null || isAllDay) {
      return undefined;
    }

    return periods.find(({ start, end }) =>
      isWithinInterval(date, { start: timestampDate(start), end: timestampDate(end) })
    )?.label;
  }
}
